/**
 * ARIA Pending Parts Page
 * Version: 1.0.0
 *
 * ARIA-6: Pending Parts
 * - Add Shipped/Received dates columns
 * - Add Schedule action button (enabled when parts received)
 * - Double-click to view job details
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Package, Calendar, Truck, CheckCircle, Clock } from 'lucide-react';
import { clsx } from 'clsx';
import type { Job, PartsStatus } from '../types';
import { IdentifierBadge } from '../components/jobs/IdentifierBadge';
import { JobDetailsModal } from '../components/jobs/JobDetailsModal';
import { SortableHeader } from '../components/table/SortableHeader';
import { compareValues, type SortDirection, type SortValueType } from '../utils/sort';

type PendingPartsSortField = 'seNumber' | 'customerName' | 'partsStatus' | 'eta' | 'shippedDate' | 'receivedDate';

const SORT_ACCESSORS: Record<PendingPartsSortField, { get: (job: Job) => unknown; type: SortValueType }> = {
  seNumber: { get: (job) => job.seNumber || job.sytelineOrderNumber, type: 'string' },
  customerName: { get: (job) => job.customerName, type: 'string' },
  partsStatus: { get: (job) => job.parts.status, type: 'string' },
  eta: { get: (job) => job.parts.eta, type: 'date' },
  shippedDate: { get: (job) => job.parts.shippedDate, type: 'date' },
  receivedDate: { get: (job) => job.parts.receivedDate, type: 'date' },
};

const PENDING_PARTS_QUERY = gql`
  query PendingPartsJobs {
    jobsPendingParts {
      id
      sytelineOrderNumber
      seNumber
      customerName
      fullAddress
      city
      identifierType
      parts {
        status
        eta
        shippedDate
        receivedDate
        notes
        transferOrders {
          id
          transferNumber
          status
          eta
          shippedDate
          receivedDate
          items {
            id
            partNumber
            partName
            qtyRequired
            qtyShipped
            qtyReceived
            status
          }
        }
      }
      jobSummary
      description
    }
  }
`;

// ARIA-6: Schedule action mutation
const SCHEDULE_FROM_PARTS_MUTATION = gql`
  mutation ScheduleFromPendingParts($id: ID!) {
    scheduleFromPendingParts(id: $id) {
      jobId
      status
      message
    }
  }
`;

const partsStatusConfig: Record<PartsStatus, { label: string; color: string; icon: typeof Package }> = {
  not_required: { label: 'Not Required', color: 'bg-gray-100 text-gray-700', icon: Package },
  available: { label: 'Available', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  partial: { label: 'Partial', color: 'bg-yellow-100 text-yellow-700', icon: Package },
  backordered: { label: 'Backordered', color: 'bg-red-100 text-red-700', icon: Clock },
  pending: { label: 'Pending', color: 'bg-orange-100 text-orange-700', icon: Clock },
  shipped: { label: 'Shipped', color: 'bg-blue-100 text-blue-700', icon: Truck },
  received: { label: 'Received', color: 'bg-green-100 text-green-700', icon: CheckCircle },
};

export default function PendingParts() {
  const { data, loading, refetch } = useQuery(PENDING_PARTS_QUERY);
  const [scheduleJob, { loading: scheduling }] = useMutation(SCHEDULE_FROM_PARTS_MUTATION);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<PendingPartsSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: string) => {
    const typedField = field as PendingPartsSortField;
    setSortDirection((prev) => (sortField === typedField && prev === 'asc' ? 'desc' : 'asc'));
    setSortField(typedField);
  };

  const jobsRaw = (data?.jobsPendingParts || []) as Job[];
  const jobs = useMemo(() => {
    if (!sortField) return jobsRaw;
    const { get, type } = SORT_ACCESSORS[sortField];
    return [...jobsRaw].sort((a, b) => compareValues(get(a), get(b), sortDirection, type));
  }, [jobsRaw, sortField, sortDirection]);

  // ARIA-6: Handle schedule action
  const handleSchedule = async (jobId: string) => {
    try {
      await scheduleJob({ variables: { id: jobId } });
      refetch();
    } catch (error) {
      console.error('Failed to schedule job:', error);
    }
  };

  // Handle double-click to open job details
  const handleRowDoubleClick = (jobId: string) => {
    setSelectedJobId(jobId);
  };

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Pending Parts</h1>
        <p className="text-gray-600">Jobs waiting on parts to be delivered</p>
      </header>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading pending parts jobs...</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No jobs waiting on parts</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <SortableHeader label="Job" field="seNumber" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Customer" field="customerName" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Parts Status" field="partsStatus" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="ETA" field="eta" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                {/* ARIA-6: Shipped Date column */}
                <SortableHeader label="Shipped" field="shippedDate" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                {/* ARIA-6: Received Date column */}
                <SortableHeader label="Received" field="receivedDate" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                {/* ARIA-6: Schedule action column — not data, not sortable */}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {jobs.map((job) => {
                const partsConfig = partsStatusConfig[job.parts.status] || partsStatusConfig.pending;
                const PartsIcon = partsConfig.icon;

                // ARIA-6: Enable schedule button only when parts received
                const canSchedule =
                  job.parts.status === 'received' || !!job.parts.receivedDate;

                return (
                  <tr
                    key={job.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onDoubleClick={() => handleRowDoubleClick(job.id)}
                    title="Double-click to view details"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {job.seNumber || job.sytelineOrderNumber}
                        </span>
                        <IdentifierBadge type={job.identifierType} />
                      </div>
                      <p className="text-xs text-gray-500">{job.city}</p>
                    </td>

                    <td className="px-4 py-3">
                      <div className="text-gray-900">{job.customerName}</div>
                      <p className="text-xs text-gray-500 truncate max-w-xs">
                        {job.jobSummary || job.description}
                      </p>
                    </td>

                    <td className="px-4 py-3">
                      <span className={clsx('badge flex items-center gap-1', partsConfig.color)}>
                        <PartsIcon className="w-3 h-3" />
                        {partsConfig.label}
                      </span>
                      {(() => {
                        const itemCount = job.parts.transferOrders.reduce((sum, to) => sum + to.items.length, 0);
                        return itemCount > 0 ? (
                          <p className="text-xs text-gray-500 mt-1">
                            {itemCount} part(s) across {job.parts.transferOrders.length} order(s)
                          </p>
                        ) : null;
                      })()}
                    </td>

                    <td className="px-4 py-3">
                      {job.parts.eta ? (
                        <div className="text-gray-900">
                          {new Date(job.parts.eta).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* ARIA-6: Shipped Date */}
                    <td className="px-4 py-3">
                      {job.parts.shippedDate ? (
                        <div className="flex items-center gap-1 text-blue-600">
                          <Truck className="w-4 h-4" />
                          {new Date(job.parts.shippedDate).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* ARIA-6: Received Date */}
                    <td className="px-4 py-3">
                      {job.parts.receivedDate ? (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          {new Date(job.parts.receivedDate).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* ARIA-6: Schedule Action Button */}
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleSchedule(job.id)}
                        disabled={!canSchedule || scheduling}
                        className={clsx(
                          'inline-flex items-center gap-1 px-3 py-1 rounded text-sm font-medium transition-colors',
                          canSchedule
                            ? 'bg-brand-600 text-white hover:bg-brand-700'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        )}
                        title={
                          canSchedule
                            ? 'Schedule this job'
                            : 'Parts must be received before scheduling'
                        }
                      >
                        <Calendar className="w-4 h-4" />
                        Schedule
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Job Details Modal */}
      <JobDetailsModal
        jobId={selectedJobId || ''}
        isOpen={!!selectedJobId}
        onClose={() => setSelectedJobId(null)}
        onJobCompleted={refetch}
      />
    </div>
  );
}
